import type { Group } from '__types__/v1/models/Group';
import isEqual from 'lodash/isEqual';
import { createLocalId } from 'v5/core/form-schema/formRows';

/** One row of the groups form: a saved group (`groupId`) or one added in this edit (`null`). */
export type GroupRow = {
  rowKey: string;
  groupId: number | null;
  name: string;
  colour: string;
};

export type GroupsFormValues = { groups: GroupRow[] };

export function groupsToRows(groups: ReadonlyArray<Group>): GroupRow[] {
  return groups.map(group => ({
    rowKey: `group-${group.id}`,
    groupId: group.id,
    name: group.name ?? '',
    colour: group.colour ?? ''
  }));
}

export function newGroupRow(name: string, colour: string): GroupRow {
  return { rowKey: createLocalId(), groupId: null, name, colour };
}

export function isBlankGroupName(name: string): boolean {
  return name.trim() === '';
}

/**
 * The colour a new group starts with: the first of the palette no group uses
 * yet; with every colour taken, the palette goes round again.
 */
export function nextGroupColour(
  palette: ReadonlyArray<string>,
  used: ReadonlyArray<string>
): string {
  const taken = new Set(used.map(colour => colour.toLowerCase()));
  const unused = palette.find(colour => !taken.has(colour.toLowerCase()));
  return unused ?? palette[used.length % palette.length];
}

/** One write a Save is made of. */
export type GroupSaveStep =
  | { type: 'archive'; groupId: number }
  | {
      type: 'update';
      groupId: number;
      patch: { name?: string; colour?: string };
    }
  | {
      type: 'create';
      rowKey: string;
      name: string;
      colour: string;
      order: number;
    }
  /** The whole order, once every row has an id. */
  | { type: 'reorder' };

/**
 * What a Save writes to turn `baseline` (the rows as saved) into `rows`, in
 * the order it writes them. A reorder follows whenever rows were added (their
 * place among the saved ones is only known once they exist), the saved ones
 * changed places, or an earlier Save created rows and never wrote the order
 * (`orderPending`).
 */
export function planGroupSave(
  baseline: ReadonlyArray<GroupRow>,
  rows: ReadonlyArray<GroupRow>,
  orderPending = false
): GroupSaveStep[] {
  const keptIds = new Set(
    rows.flatMap(row => (row.groupId === null ? [] : [row.groupId]))
  );
  const archive = baseline.flatMap((saved): GroupSaveStep[] =>
    saved.groupId !== null && !keptIds.has(saved.groupId)
      ? [{ type: 'archive', groupId: saved.groupId }]
      : []
  );
  const update = rows.flatMap((row): GroupSaveStep[] => {
    const saved = baseline.find(
      candidate => row.groupId !== null && candidate.groupId === row.groupId
    );
    if (saved === undefined || row.groupId === null) return [];
    const name = row.name.trim();
    const patch = {
      ...(name !== saved.name.trim() && { name }),
      ...(row.colour !== saved.colour && { colour: row.colour })
    };
    return Object.keys(patch).length === 0
      ? []
      : [{ type: 'update', groupId: row.groupId, patch }];
  });
  const create = rows.flatMap((row, index): GroupSaveStep[] =>
    row.groupId === null
      ? [
          {
            type: 'create',
            rowKey: row.rowKey,
            name: row.name.trim(),
            colour: row.colour,
            order: index
          }
        ]
      : []
  );
  const savedOrder = baseline.flatMap(saved =>
    saved.groupId !== null && keptIds.has(saved.groupId) ? [saved.groupId] : []
  );
  const rowsOrder = rows.flatMap(row =>
    row.groupId === null ? [] : [row.groupId]
  );
  const reorder: GroupSaveStep[] =
    orderPending || create.length > 0 || !isEqual(savedOrder, rowsOrder)
      ? [{ type: 'reorder' }]
      : [];
  return [...archive, ...update, ...create, ...reorder];
}

/**
 * The baseline once `step` is written: a Save that stops halfway resumes from
 * what is already saved. `createdId` is the id the server gave a created row;
 * `rows` is the order a reorder wrote.
 */
export function settleGroupSaveStep(
  baseline: ReadonlyArray<GroupRow>,
  step: GroupSaveStep,
  context: { rows: ReadonlyArray<GroupRow>; createdId?: number }
): GroupRow[] {
  switch (step.type) {
    case 'archive':
      return baseline.filter(saved => saved.groupId !== step.groupId);
    case 'update':
      return baseline.map(saved =>
        saved.groupId === step.groupId
          ? {
              ...saved,
              ...(step.patch.name !== undefined && { name: step.patch.name }),
              ...(step.patch.colour !== undefined && {
                colour: step.patch.colour
              })
            }
          : saved
      );
    case 'create':
      return [
        ...baseline,
        {
          rowKey: step.rowKey,
          groupId: context.createdId ?? null,
          name: step.name,
          colour: step.colour
        }
      ];
    case 'reorder': {
      const position = (row: GroupRow) =>
        context.rows.findIndex(candidate => candidate.groupId === row.groupId);
      return [...baseline].sort((a, b) => position(a) - position(b));
    }
  }
}
